import React, { useState } from "react";
import {
  CreditCard,
  FileText,
  Send,
  CheckCircle,
  Bike,
  Footprints,
  AlertCircle,
  Heart,
  Calendar,
  Shield,
  Award,
  Clock,
  Phone,
  Mail,
  ExternalLink,
  Users,
  Star,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Download,
  Smartphone,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/home-slider-01-tesseramento-2026.jpg";

// ============================================
// LINK MODULI GOOGLE DRIVE
// ============================================

const LINKS = {
  nuovaIscrizione: "https://drive.google.com/file/d/1V3cYUr0SvZSwHWEe_e3wvfgSqSSQb3SH/view?usp=sharing",
  rinnovoIscrizione: "https://drive.google.com/file/d/1In7wUbXoHT7txcYjSd_XIdP5Wd46X3JE/view?usp=sharing",
  facsimileCertificato:
    "https://www.gazzettaufficiale.it/do/atto/serie_generale/caricaPdf?cdimg=13A0631300500010110001&dgu=2013-07-20&art.dataPubblicazioneGazzetta=2013-07-20&art.codiceRedazionale=13A06313&art.num=1&art.tiposerie=SG",
  normativaDM: "https://www.gazzettaufficiale.it/eli/id/2013/07/20/13A06313/sg",
  trasparenza: "https://www.99castelli.it/trasparenza",
  documenti: "/documenti",
};

// ============================================
// DATI TESSERAMENTI ESPANDIBILI
// ============================================

const tesseramentiEspandibili = [
  {
    id: "cicloturista",
    title: "Tesseramento annuale Cicloturista",
    price: "45€",
    scadenza: "31 dicembre",
    content: (
      <>
        <p className="mb-4">
          Permette al tesserato di partecipare alle uscite cicloturistiche organizzate da ASD 99 Castelli e alle
          manifestazioni cicloturistiche organizzate da Associazioni affiliate CSI o ad altri Enti di Promozione
          Sportiva Convenzionati FCI.
        </p>
        <p className="mb-4">
          Il cicloturista può partecipare a manifestazioni con percorrenze di <strong>massimo 60 km</strong> e
          dislivello massimo pari all'1% della lunghezza totale del percorso, come da Regolamento Tecnico Ciclismo del
          CSI - Centro Sportivo Italiano.
        </p>
        <p className="mb-4">
          Il costo del tesseramento Cicloturista per l'anno 2025 è di <strong>45€</strong> (scadenza 31 dicembre) ed
          include, oltre l'assicurazione, tutte le uscite cicloturistiche che verranno organizzate da 99 Castelli.
        </p>
        <p className="font-semibold underline mb-2">Nel costo, NON è compreso:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>
            l'iscrizione per la partecipazione ad eventi/manifestazioni cicloturistiche organizzate da altre
            associazioni/enti;
          </li>
          <li>eventuale Guida Turistica o di montagna, che potrebbe essere prevista in alcune attività.</li>
        </ul>
        <p className="mb-2">
          Per l'attività di cicloturismo è necessario presentare in fase di tesseramento il{" "}
          <strong>Certificato di idoneità alla pratica sportiva non agonistica</strong> (da non confondere con quello
          per attività ludico-motoria) rilasciato dal Medico di Medicina Generale: senza non sarà possibile praticare le
          nostre attività e non sarà possibile attivare la copertura assicurativa.
        </p>
        <p>
          <a
            href={LINKS.facsimileCertificato}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:text-primary-dark"
          >
            Premendo qui troverete
          </a>{" "}
          un facsimile di tale certificato. La normativa di riferimento è il{" "}
          <a
            href={LINKS.normativaDM}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:text-primary-dark"
          >
            D.M. del 24/04/2013
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "ciclista-amatore",
    title: "Tesseramento annuale Ciclista Amatore (agonista)",
    price: "55€",
    scadenza: "31 dicembre",
    content: (
      <>
        <p className="mb-4">
          Permette al tesserato di partecipare alle manifestazioni, anche agonistiche, organizzate da Associazioni
          affiliate CSI o ad altri Enti di Promozione Sportiva Convenzionati FCI.
        </p>
        <p className="mb-4">
          Il Ciclista Amatore può partecipare a manifestazioni <strong>senza limiti di percorrenza e dislivello</strong>
          , come da Regolamento Tecnico Ciclismo del CSI - Centro Sportivo Italiano.
        </p>
        <p className="font-semibold underline mb-2">Nel costo, NON è compreso:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>
            l'iscrizione per la partecipazione ad eventi/manifestazioni cicloturistiche organizzate da altre
            associazioni/enti;
          </li>
          <li>eventuale Guida Turistica o di montagna, che potrebbe essere prevista in alcune attività.</li>
        </ul>
        <p className="mb-4">
          Il suo costo per l'anno 2025 è di <strong>55€</strong> (scadenza 31 dicembre) ed include, oltre
          l'assicurazione, tutte le uscite cicloturistiche che verranno organizzate da 99 Castelli.
        </p>
        <p>
          Per le attività di Ciclista amatore è necessario presentare in fase di tesseramento il{" "}
          <strong>Certificato di idoneità alla pratica sportiva agonistica del ciclismo</strong> (da non confondere con
          quello per la pratica sportiva non agonistica) rilasciato da un Medico Specializzato in Medicina Sportiva.
        </p>
      </>
    ),
  },
  {
    id: "camminatore",
    title: "Tesseramento annuale Camminatore",
    price: "20€",
    scadenza: "31 dicembre",
    content: (
      <>
        <p className="mb-4">
          Permette al tesserato di partecipare alle uscite con il Gruppo di Cammino organizzato dall'Associazione.
        </p>
        <p className="mb-4">
          Il suo costo per l'anno 2025 è di <strong>20€</strong> e comprende oltre l'assicurazione le uscite a piedi che
          verranno effettuate.
        </p>
        <p className="mb-4">
          <span className="font-semibold underline">Non è compreso</span> il costo di un'eventuale Guida Turistica o di
          montagna, che potrebbe essere prevista in alcune attività.
        </p>
        <p>
          Essendo l'attività Gruppo di Cammino considerata a basso impegno fisico,{" "}
          <strong>non è necessaria la certificazione sanitaria</strong>, anche se consigliamo di farsi rilasciare almeno
          il certificato per attività ludico-motoria dal Medico di Medicina Generale.
        </p>
      </>
    ),
  },
  {
    id: "ciclista-camminatore",
    title: "Tesseramento annuale Ciclista (Cicloturista ed Amatore) e Camminatore",
    price: "60€",
    scadenza: "31 dicembre",
    content: (
      <>
        <p className="mb-4">
          Permette al tesserato di avere un <strong>costo agevolato</strong> tesserandosi nel contempo come Ciclista
          (Cicloturista o Ciclista Amatore a seconda delle attività che si desidera fare) che come Camminatore: per i
          dettagli leggere le specifiche sezioni.
        </p>
        <p>
          Il suo costo per l'anno 2025 è di <strong>60€</strong> l'anno.
        </p>
      </>
    ),
  },
  {
    id: "sostenitore",
    title: "Tesseramento Sostenitore",
    price: "Libero",
    scadenza: "-",
    content: (
      <>
        <p className="mb-4">
          Permette al tesserato che non vuole o non può partecipare alle nostre attività di sostenere comunque
          l'Associazione nella sua attività di ricerca e sensibilizzazione.
        </p>
        <p>
          Il Sostenitore può decidere di sostenerci con una donazione in denaro, con una sponsorizzazione o con un aiuto
          concreto di qualunque tipo (ad esempio donazione di pubblicazioni, aiuto nella gestione di attività, aiuto
          nella ricerca dei Castelli ecc...).
        </p>
      </>
    ),
  },
  {
    id: "giornaliero",
    title: "Tesseramento Giornaliero",
    price: "Variabile",
    scadenza: "Giornata",
    content: (
      <>
        <p className="mb-4">
          Permette alla persona che vuole provare una delle nostre attività di tesserarsi per un solo giorno, avendo
          comunque una copertura assicurativa. È previsto per le attività del Gruppo di Cammino e per quelle relative al
          Cicloturismo.
        </p>
        <p className="font-semibold underline mb-2">Nel tesseramento giornaliero non è compreso:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>
            Il costo di un'eventuale Guida Turistica o di montagna, che potrebbe essere prevista in alcune attività.
          </li>
          <li>Tutti i benefici riportati in basso relativi al tesseramento annuale.</li>
        </ul>
        <p className="font-semibold mb-4">
          Le modalità ed i costi per il tesseramento giornaliero verranno riportati per ogni evento.
        </p>
        <p className="mb-2">
          Per il tesseramento giornaliero del Gruppo di Cammino, essendo considerata un'attività a basso impegno fisico,
          non è necessaria la certificazione sanitaria, anche se consigliamo di farsi rilasciare almeno il certificato
          per attività ludico-motoria dal Medico di Medicina Generale.
        </p>
        <p>
          Per il tesseramento giornaliero per attività del Cicloturismo, è richiesto almeno un certificato in corso di
          validità di tipo Non Agonistico.
        </p>
      </>
    ),
  },
];

const vantaggi = [
  "costo riservato ai tesserati per eventuali uscite dove è prevista una guida turistica o di montagna;",
  "possibilità di accedere alla piattaforma My CSI per scoprire tutte le convenzioni, sconti e offerte su un'ampia gamma di prodotti e servizi dei più prestigiosi marchi e delle migliori aziende;",
  "convenzione con la Clinica Villa Letizia di Preturo per effettuare a costi vantaggiosi una visita sportiva, agonistica o non agonistica, necessaria per le attività in bicicletta ed altamente consigliata per le camminate;",
  "possibilità di espandere la copertura assicurativa a costi agevolati anche ad altri sport, ma anche ad altri ambiti della vita quotidiana. Ad esempio tesserandoti con noi potrai acquistare a costi agevolati una polizza per sciare, ormai obbligatoria, ma anche una RCA grazie ad una convenzione.",
];

// ============================================
// COMPONENTE ACCORDION
// ============================================

interface AccordionItemProps {
  title: string;
  price: string;
  scadenza: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, price, scadenza, children, isOpen, onToggle }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors text-left"
    >
      <div className="flex-1">
        <h3 className="text-lg font-heading font-semibold text-primary">{title}</h3>
        {price !== "Libero" && price !== "Variabile" && (
          <p className="text-sm text-text-light mt-1">
            Costo: <strong>{price}</strong> | Scadenza: {scadenza}
          </p>
        )}
      </div>
      <div className="ml-4 flex-shrink-0">
        {isOpen ? <ChevronUp className="w-6 h-6 text-primary" /> : <ChevronDown className="w-6 h-6 text-primary" />}
      </div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="p-4 pt-0 text-text-light bg-gray-50">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

const Tesseramento: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  return (
    <PageLayout>
      {/* Hero Banner - Immagine intera */}
      <div className="w-full">
        <img src={HERO_IMAGE} alt="Tesseramento 2026" className="w-full h-auto" />
      </div>

      {/* Titolo e Sottotitolo */}
      <Section background="white" className="py-10 md:py-14">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
            Tesseramento ASD 99 Castelli
          </h1>
          <p className="text-lg sm:text-xl text-text-light">
            Unisciti alla nostra comunità e scopri i 99 Castelli insieme a noi
          </p>
        </div>
      </Section>

      {/* Intro e Pulsanti Moduli */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-text-light text-lg mb-4">
              Da questa pagina è possibile effettuare una <strong className="text-primary">Nuova iscrizione</strong>,
              per chi entra a far parte di ASD 99 Castelli per la prima volta, oppure{" "}
              <strong className="text-primary">Rinnovare iscrizione</strong> per chi è già tesserato.
            </p>
            <p className="font-semibold text-foreground">
              Leggere attentamente quanto segue prima di procedere alla nuova/rinnovo di iscrizione
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Button href={LINKS.nuovaIscrizione} variant="primary" size="lg" icon={<Download size={20} />}>
              Nuova iscrizione
            </Button>
            <Button href={LINKS.rinnovoIscrizione} variant="secondary" size="lg" icon={<Download size={20} />}>
              Rinnovare iscrizione
            </Button>
          </div>

          {/* QR Codes */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Smartphone size={20} className="text-primary" />
              <p className="text-sm text-text-light text-center">
                Scansiona il QR code con il tuo smartphone per scaricare direttamente il modulo
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {/* QR Nuova Iscrizione */}
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-xl shadow-elegant border border-gray-100">
                  <QRCodeSVG
                    value={LINKS.nuovaIscrizione}
                    size={140}
                    level="M"
                    includeMargin={false}
                    fgColor="#1a365d"
                  />
                </div>
                <p className="mt-3 text-sm font-medium text-primary">Nuova iscrizione</p>
              </div>

              {/* QR Rinnovo Iscrizione */}
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-xl shadow-elegant border border-gray-100">
                  <QRCodeSVG
                    value={LINKS.rinnovoIscrizione}
                    size={140}
                    level="M"
                    includeMargin={false}
                    fgColor="#8B6914"
                  />
                </div>
                <p className="mt-3 text-sm font-medium text-secondary">Rinnovare iscrizione</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Documenti da inviare */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="font-semibold text-foreground mb-4">
              In entrambi i casi il tesseramento verrà attivato nel momento il cui riceveremo all'indirizzo Email{" "}
              <a href="mailto:99castelli@gmail.com" className="text-primary underline">
                99castelli@gmail.com
              </a>
              :
            </p>
            <ul className="space-y-2 text-text-light">
              <li className="flex items-start gap-2">
                <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span>
                    ricevuta del pagamento effettuato sull'iban <strong>IT74T0832740700000000008756</strong>, con
                    causale:
                  </span>
                  <ol className="list-decimal ml-6 mt-2 space-y-1">
                    <li>Tesseramento Anno</li>
                    <li>Nome</li>
                    <li>Cognome</li>
                  </ol>
                  <p className="mt-2 text-sm text-text-light italic">Esempio: Tesseramento 2026 Mario Rossi</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>il modulo debitamente compilato e firmato;</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>
                  copia del documento di identità indicato nel modulo di iscrizione, se si tratta di una Nuova
                  iscrizione o il documento precedente risulta essere scaduto;
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                <span>copia del certificato medico (ove previsto).</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Perché Tesserarsi */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto">
          <SectionHeader title="Perché Tesserarsi con ASD 99 Castelli" align="left" />
          <div className="prose prose-lg max-w-none text-text-light">
            <p>
              Per poter partecipare alle attività di ASD 99 Castelli e viverle in tutta serenità è necessario effettuare
              un tesseramento, che include una <strong className="text-foreground">copertura assicurativa</strong> ed
              una serie di altri vantaggi.
            </p>
            <p>
              Per questo ci siamo affiliati al{" "}
              <strong className="text-foreground">CSI - Centro Sportivo Italiano</strong>, ente di promozione sportiva e
              sociale con cui condividiamo le finalità di promozione dello sport come momento di educazione, di
              crescita, di impegno e di aggregazione sociale che ci permette di fornire ai nostri tesserati una{" "}
              <strong className="text-foreground">copertura assicurativa</strong> di base, espandibile con eventuali
              ulteriori garanzie,{" "}
              <strong className="text-foreground">nello svolgere le attività da noi proposte</strong>.
            </p>
            <p>
              Nella pagina{" "}
              <a href={LINKS.documenti} className="text-primary underline">
                Documenti
              </a>{" "}
              troverai tutta le informazioni necessarie.
            </p>
            <p>
              Considerando le tipologie delle nostre attività, abbiamo deciso di prevedere{" "}
              <strong className="text-foreground">diversi tipi di tesseramento</strong>:
            </p>
          </div>
        </div>
      </Section>

      {/* Tipi di Tesseramento (Accordion) */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          {tesseramentiEspandibili.map((t) => (
            <AccordionItem
              key={t.id}
              title={t.title}
              price={t.price}
              scadenza={t.scadenza}
              isOpen={openAccordion === t.id}
              onToggle={() => toggleAccordion(t.id)}
            >
              {t.content}
            </AccordionItem>
          ))}
        </div>
      </Section>

      {/* Vantaggi del Tesseramento Annuale */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto">
          <SectionHeader title="Vantaggi del Tesseramento Annuale" align="left" />
          <p className="text-text-light mb-4">Il tesseramento annuale porta innumerevoli vantaggi:</p>
          <ul className="space-y-3">
            {vantaggi.map((v, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle size={20} className="text-secondary mt-0.5 flex-shrink-0" />
                <span className="text-text-light">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Note Legali */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 rounded-xl p-6">
            <p className="text-text-light">
              <strong className="text-foreground">Tesserandoti ti verrà richiesto di accettare</strong> lo Statuto, le
              condizioni e l'Informativa Privacy (GDPR) presenti integralmente presso la sede sociale e consultabili sul
              sito web all'indirizzo{" "}
              <a
                href={LINKS.trasparenza}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:text-primary-dark"
              >
                www.99castelli.it/trasparenza
              </a>
            </p>
          </div>
        </div>
      </Section>

      {/* Footer CTA */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">Hai Domande?</h2>
          <p className="text-white/90 text-lg mb-8">
            Contattaci per qualsiasi informazione sul tesseramento. Siamo qui per aiutarti!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              href="https://wa.me/393402894567?text=Ciao!%20Vorrei%20informazioni%20sul%20tesseramento%20di%2099%20Castelli"
              variant="secondary"
              size="lg"
              icon={<Phone size={20} />}
            >
              WhatsApp: 340 2894567
            </Button>
            <Button
              href="mailto:99castelli@gmail.com"
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              icon={<Mail size={20} />}
            >
              99castelli@gmail.com
            </Button>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default Tesseramento;
